
import React, { useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import HomeSectionCard from "../HomeSectionCard/HomeSectionCard";
import { CustomNextArrowForPopularStyle, CustomPrevArrow } from "../Home/SettingsAndArrows";
import useUIStyles from "../../../Hooks/useUIStyles";

const PopularStyles = ({ categories, products }) => {
  const { heading, title, description } = useUIStyles();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const allProducts = products?.products?.response || [];

  const filteredProducts =
    selectedCategory === "All" || selectedCategory === "New" || selectedCategory === "Designers"
      ? allProducts
      : allProducts.filter((product) => product?.category?.name === selectedCategory);

  while (filteredProducts?.length < 4 && filteredProducts?.length > 0) {
    filteredProducts?.push(<div key={`placeholder-${filteredProducts?.length}`} className="opacity-10" />);
  }

  const settings = {
    dots: false,
    infinite: false,
    arrows: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 3,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    prevArrow: <CustomPrevArrow currentSlide={currentSlide} />,
    nextArrow: (
      <CustomNextArrowForPopularStyle
        currentSlide={currentSlide}
        slideCount={filteredProducts?.length || 0}
      />
    ),
    responsive: [
      {
        breakpoint: 1440,
        settings: { slidesToShow: 4, slidesToScroll: 3 },
      },
      {
        breakpoint: 1200,
        settings: { slidesToShow: 3.5, slidesToScroll: 3 },
      },
      {
        breakpoint: 1024,
        settings: { slidesToShow: 3, slidesToScroll: 2 },
      },
      {
        breakpoint: 900,
        settings: { slidesToShow: 2.5, slidesToScroll: 2 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 2, slidesToScroll: 1 },
      },
      {
        breakpoint: 640,
        settings: { slidesToShow: 1.5, slidesToScroll: 1 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1, slidesToScroll: 1 },
      },
    ],
  };

  const displayedCategories = showAllCategories
    ? categories?.categories?.response
    : categories?.categories?.response;

  return (
    <div className="py-10 px-3 sm:px-4 bg-white">
      <div className="max-w-7xl mx-auto bg-cream rounded-[28px] py-8 px-4 sm:px-8">
        <div className="mb-8">
          <h2 className="section-heading text-[28px] md:text-[32px]">
            Popular Styles
          </h2>
          <p className="text-stone-500 mt-2 max-w-2xl">
            Discover trending styles and fashion-forward designs
          </p>
        </div>

        <div className="bg-white rounded-[28px] overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Enhanced Category Filters */}
            <div className="lg:w-64 p-6 bg-cream text-stone-800">
              <h3 className="font-display text-xl mb-5">Categories</h3>

              <div className="space-y-2">
                <button
                  className={`w-full py-3 px-4 text-left font-medium transition-all duration-300 transform hover:scale-105 ${selectedCategory === "All"
                      ? "bg-gray-800 text-white shadow-lg"
                      : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
                    }`}
                  onClick={() => setSelectedCategory("All")}
                  style={{ borderRadius: '0' }}
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="text-sm">All Styles</span>
                  </span>
                </button>

                {displayedCategories?.slice(0, 6)?.map((cat) => (
                  <button
                    key={cat?.id}
                    className={`w-full py-2.5 px-4 text-left font-medium transition-all duration-300 transform hover:scale-105 ${selectedCategory === cat?.name
                        ? "bg-gray-800 text-white shadow-lg"
                        : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
                      }`}
                    onClick={() => setSelectedCategory(cat?.name)}
                    style={{ borderRadius: '0' }}
                  >
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-sm">{cat?.name}</span>
                    </span>
                  </button>
                ))}
              </div>

              {categories?.categories?.response?.length > 6 && (
                <button
                  onClick={() => setShowAllCategories((prev) => !prev)}
                  className="w-full mt-4 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-all duration-300 transform hover:scale-105 text-sm"
                  style={{ borderRadius: '0' }}
                >
                  {showAllCategories ? "Show Less" : "View All Categories"}
                </button>
              )}
            </div>

            {/* Enhanced Product Carousel */}
            <div className="lg:w-3/4 p-5">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  {selectedCategory === "All" ? "All Popular Styles" : `${selectedCategory} Styles`}
                </h3>
                <p className="text-sm text-gray-600">
                  {filteredProducts?.length || 0} styles available
                </p>
              </div>

              {filteredProducts?.length > 0 ? (
                <div className="relative">
                  <Slider {...settings}>
                    {filteredProducts?.map((product) => (
                      <div key={product.id}>
                        <HomeSectionCard product={product} />
                      </div>
                    ))}
                  </Slider>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                  <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                  </svg>
                  <p className="text-base">No products available in this category.</p>
                  <p className="text-sm">Try selecting a different category.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopularStyles;